
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { 
  Book, 
  FileText, 
  Plus, 
  Search, 
  Languages, 
  ChevronLeft,
  MenuIcon,
  BarChart3,
  X,
  Brain,
  LayoutGrid,
  LayoutList
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAdmin } from "@/hooks/useAdmin";
import { StudyNoteCard } from "./StudyNoteCard";
import { StudyNoteUploader } from "./StudyNoteUploader";
import { StudyNoteViewer } from "./StudyNoteViewer";
import { StudyNote } from "@/types/notes";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";
import LanguageSelector from "../flashcards/LanguageSelector";
import SWOTAnalysis from "./SWOTAnalysis";
import AdaptiveNotesGenerator from "./AdaptiveNotesGenerator";
import { useAdaptiveNotes } from "@/hooks/useAdaptiveNotes";
import { ScrollArea } from "@/components/ui/scroll-area";
import { StatsCards } from "./StatsCards";

const StudyNotes = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [notes, setNotes] = useState<StudyNote[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState<StudyNote | null>(null);
  const [isUploaderOpen, setIsUploaderOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Default to collapsed on mobile
  const [activeLanguage, setActiveLanguage] = useState<string>("all");
  const [showStats, setShowStats] = useState(false); // Default to hidden
  const [showAdaptive, setShowAdaptive] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { isAdmin } = useAdmin();
  const { isAuthenticated } = useAuthSession();
  const { swotAnalysis } = useAdaptiveNotes();
  const navigate = useNavigate();

  // Effect to check screen size and adjust UI accordingly
  useEffect(() => {
    const handleResize = () => {
      // For better mobile UX, always start with collapsed sidebar and hidden stats on mobile
      const isMobileView = window.innerWidth < 768;
      setSidebarCollapsed(isMobileView);
      setShowStats(false); // Default hidden on both mobile and desktop for more content space
    };
    
    // Set initial values
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    fetchNotes();
  }, [isAuthenticated, navigate]);

  const fetchNotes = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("study_notes")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data) {
        const formattedNotes = data.map((note) => ({
          id: note.id,
          title: note.title,
          content: note.content,
          subject: note.subject,
          category: note.category,
          fileUrl: note.file_url,
          contentType: note.content_type || "text",
          language: note.language || "en",
          createdAt: note.created_at,
          updatedAt: note.updated_at,
          adaptiveAnalysis: note.adaptive_analysis
        }));
        
        setNotes(formattedNotes);
        
        const uniqueCategories = [...new Set(data.map((note) => note.category))];
        setCategories(["all", ...uniqueCategories]);
      }
    } catch (error) {
      console.error("Error fetching study notes:", error);
      toast.error("Failed to load study notes");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!isAdmin) {
      toast.error("Only administrators can delete notes");
      return;
    }

    try {
      const { error } = await supabase
        .from("study_notes")
        .delete()
        .eq("id", noteId);

      if (error) throw error;

      toast.success("Note deleted successfully");
      setNotes(notes.filter((note) => note.id !== noteId));
      
      if (selectedNote?.id === noteId) {
        setSelectedNote(null);
      }
    } catch (error) {
      console.error("Error deleting study note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleNoteCreated = (newNote: StudyNote) => {
    setNotes([newNote, ...notes]);
    
    if (!categories.includes(newNote.category)) {
      setCategories([...categories, newNote.category]);
    }
    
    setIsUploaderOpen(false);
    toast.success("Study note added successfully");
  };

  const handleAdaptiveNoteCreated = (noteId: string) => {
    fetchNotes().then(() => {
      const newNote = notes.find(note => note.id === noteId);
      if (newNote) {
        setSelectedNote(newNote);
      }
    });
  };

  const handleNoteSelect = (note: StudyNote) => {
    setSelectedNote(note);
    // On mobile, collapse sidebar when a note is selected
    if (window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
  };

  const toggleAdaptive = () => {
    setShowAdaptive(!showAdaptive);
  };

  const filteredNotes = notes.filter((note) => {
    const matchesCategory = activeTab === "all" || note.category === activeTab;
    const matchesLanguage = activeLanguage === "all" || note.language === activeLanguage;
    const matchesSearch = 
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.subject.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesLanguage && matchesSearch;
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const sortedNotes = [...filteredNotes].sort((a, b) => {
    // Sort by most recent first
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  // Function to show notes list (mobile friendly)
  const showNotesList = () => {
    setSelectedNote(null);
    setSidebarCollapsed(false);
  };
  
  // Simplified header with minimal controls for mobile
  const renderHeader = () => (
    <div className="px-2 sm:px-4 py-2 border-b bg-white shadow-sm sticky top-0 z-10">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={toggleSidebar}
          >
            {sidebarCollapsed ? <MenuIcon className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </Button>
          
          <h1 className="text-base sm:text-lg font-medium truncate">
            {showAdaptive ? "Adaptive Notes" : "Study Notes"}
          </h1>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="h-7 w-7 sm:h-8 sm:w-8 p-1"
          >
            {viewMode === 'grid' ? 
              <LayoutList className="h-3.5 w-3.5" /> : 
              <LayoutGrid className="h-3.5 w-3.5" />}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAdaptive}
            className="h-7 w-7 sm:h-8 sm:w-8 p-1"
          >
            {showAdaptive ? 
              <FileText className="h-3.5 w-3.5" /> : 
              <Brain className="h-3.5 w-3.5" />}
          </Button>
          
          <Button
            variant="outline" 
            size="sm"
            onClick={() => setShowStats(!showStats)}
            className="h-7 w-7 sm:h-8 sm:w-8 p-1"
          >
            <BarChart3 className="h-3.5 w-3.5" />
          </Button>
          
          {isAdmin && (
            <Button 
              onClick={() => setIsUploaderOpen(true)} 
              size="sm"
              className="h-7 px-2 text-xs bg-secondary hover:bg-secondary/90 text-white"
            >
              <Plus className="mr-1 h-3 w-3" />
              Add
            </Button>
          )}
        </div>
      </div>
      
      {showStats && (
        <div className="mt-2">
          <StatsCards 
            notes={notes} 
            categories={categories} 
            isAdmin={isAdmin}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full h-[calc(100vh-8rem)] flex flex-col overflow-hidden">
      {renderHeader()}
      
      <div className="flex flex-1 h-[calc(100vh-12rem)] overflow-hidden">
        {!sidebarCollapsed && (
          <div className="w-full sm:w-72 border-r bg-gray-50/70 flex-shrink-0 transition-all duration-300 flex flex-col h-full overflow-hidden">
            <div className="p-2 border-b">
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search notes..."
                  className="pl-7 h-7 text-xs border-gray-200"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center">
                  <Languages className="h-3 w-3 mr-1 text-secondary" />
                  <span className="text-xs font-medium">Language</span>
                </div>
                <LanguageSelector 
                  currentLanguage={activeLanguage}
                  onLanguageChange={setActiveLanguage}
                  variant="minimal"
                  className="h-6 text-xs"
                />
              </div>
            </div>

            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="justify-start mb-1 bg-gray-100/70 p-0.5 overflow-x-auto h-8">
                {categories.map((category) => (
                  <TabsTrigger 
                    key={category} 
                    value={category} 
                    className="capitalize text-[10px] h-6"
                  >
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>

              <ScrollArea className="flex-1 px-2 py-1">
                {isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((n) => (
                      <div key={n} className="h-16 rounded-md bg-gray-200 animate-pulse" />
                    ))}
                  </div>
                ) : sortedNotes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-3 text-center text-muted-foreground bg-white rounded-lg border border-dashed border-gray-300">
                    <FileText className="h-5 w-5 mb-1 text-gray-400" />
                    <p className="text-xs font-medium text-gray-500">No notes found</p>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        className="mt-2 h-7 text-xs" 
                        size="sm"
                        onClick={() => setIsUploaderOpen(true)}
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add Note
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 pb-4">
                    {sortedNotes.map((note) => (
                      <StudyNoteCard
                        key={note.id}
                        note={note}
                        isSelected={selectedNote?.id === note.id}
                        onSelect={() => handleNoteSelect(note)}
                        onDelete={handleDeleteNote}
                        isAdmin={isAdmin}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </Tabs>
          </div>
        )}

        <div className="flex-1 relative h-full overflow-hidden bg-white">
          {/* When a note is selected, show a back button on mobile */}
          {selectedNote && window.innerWidth < 768 && (
            <div className="p-2 bg-white sticky top-0 z-10 border-b">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={showNotesList}
                className="text-xs h-7 px-2"
              >
                <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Back
              </Button>
            </div>
          )}
          
          {showAdaptive && !selectedNote && (
            <div className="h-full p-3 overflow-auto">
              <div className="space-y-3">
                <AdaptiveNotesGenerator onNoteGenerated={handleAdaptiveNoteCreated} />
                <SWOTAnalysis analysis={swotAnalysis} />
              </div>
            </div>
          )}

          {selectedNote ? (
            <StudyNoteViewer note={selectedNote} onBack={showNotesList} />
          ) : !showAdaptive ? (
            <ScrollArea className="h-full p-2 sm:p-4">
              {sortedNotes.length > 0 ? (
                <div className={viewMode === 'grid' ? 
                  "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" : 
                  "space-y-3"
                }>
                  {sortedNotes.map(note => (
                    <Card 
                      key={note.id}
                      className="overflow-hidden hover:shadow-md transition-all cursor-pointer border-secondary/10"
                      onClick={() => setSelectedNote(note)}
                    >
                      <div className="h-1 bg-gradient-to-r from-secondary to-primary" />
                      <div className="p-3">
                        <h3 className="text-sm font-medium truncate mb-1">{note.title}</h3>
                        <div className="flex flex-wrap items-center gap-1 mb-1">
                          <span className="text-[10px] bg-secondary/10 text-secondary px-1.5 py-0.5 rounded-full">
                            {note.subject}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {note.content.replace(/<[^>]*>?/gm, '').substring(0, 80)}...
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center py-6 h-full">
                  <Book className="h-8 w-8 text-gray-300 mb-2" />
                  <h3 className="text-base font-medium text-gray-700 mb-1">No Study Notes</h3>
                  <p className="text-xs text-gray-500 max-w-xs mb-3">
                    Start building your knowledge collection
                  </p>
                  {isAdmin && (
                    <Button 
                      onClick={() => setIsUploaderOpen(true)} 
                      size="sm"
                      className="text-xs"
                    >
                      <Plus className="mr-1.5 h-3 w-3" /> Add Note
                    </Button>
                  )}
                </div>
              )}
            </ScrollArea>
          ) : null}
        </div>
      </div>

      {isUploaderOpen && (
        <StudyNoteUploader
          isOpen={isUploaderOpen}
          onClose={() => setIsUploaderOpen(false)}
          onNoteCreated={handleNoteCreated}
        />
      )}
    </div>
  );
};

export default StudyNotes;
